import React from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useWorkspace } from '../store/workspaceStore';
import { Lead, LeadStatus } from '../types';
import { Topbar } from '../components/layout/Topbar';
import { Avatar } from '../components/common/Avatar';
const COLUMNS: {
  key: LeadStatus;
  label: string;
  accent: string;
}[] = [{
  key: 'new',
  label: 'New',
  accent: 'bg-slate-400'
}, {
  key: 'contacted',
  label: 'Contacted',
  accent: 'bg-blue-500'
}, {
  key: 'qualified',
  label: 'Qualified',
  accent: 'bg-violet-500'
}, {
  key: 'won',
  label: 'Won',
  accent: 'bg-emerald-500'
}, {
  key: 'lost',
  label: 'Lost',
  accent: 'bg-rose-500'
}];
const ADVANCE: Record<LeadStatus, LeadStatus | null> = {
  new: 'contacted',
  contacted: 'qualified',
  qualified: 'won',
  won: null,
  lost: null
};
export function LeadsBoard() {
  const leads = useWorkspace(s => s.leads);
  const setStatus = useWorkspace(s => s.setLeadStatus);
  const personById = useWorkspace(s => s.personById);
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-209" data-mp="2akd6hs6z0lg3">
      <Topbar title="Leads" subtitle="Pipeline of owners and investors, by stage." data-id="element-210" data-mp="25ls5vrnt3zr4" />

      <div className="mp-scroll flex-1 overflow-x-auto p-6" data-id="element-211" data-mp="2k9n2vk8ukqa0">
        <div className="flex min-w-max gap-4" data-id="element-212" data-mp="2kbzr2f577wvi">
          {COLUMNS.map(col => {
          const items = leads.filter(l => l.status === col.key);
          return <div key={col.key} className="flex w-72 shrink-0 flex-col" data-id="element-213" data-mp="2t9a5uah86456">
                <div className="mb-2 flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow-card" data-id="element-214" data-mp="2p9rgn2idf6wj">
                  <div className="flex items-center gap-2" data-id="element-215" data-mp="337g3ugzwuq4r">
                    <span className={`h-2.5 w-2.5 rounded-full ${col.accent}`} data-id="element-216" data-mp="2y9xs6iuidwt2" />
                    <span className="text-sm font-semibold text-slate-700" data-id="element-217" data-mp="2y7v5a7onkjsk">{col.label}</span>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500" data-id="element-218" data-mp="13vwtbxyyl9sz">{items.length}</span>
                </div>

                <div className="space-y-2.5" data-id="element-219" data-mp="33zh9fs2taw67">
                  {items.map(lead => <LeadCard key={lead.id} lead={lead} owner={personById(lead.ownerId)} onAdvance={() => {
                const next = ADVANCE[lead.status];
                if (next) setStatus(lead.id, next);
              }} data-id="element-220" data-mp="38xsl88co3gr2" />)}
                  {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-300" data-id="element-221" data-mp="3mwifzhc0l4c9">Empty</div>}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}
function LeadCard({
  lead,
  owner,
  onAdvance
}: {
  lead: Lead;
  owner: ReturnType<ReturnType<typeof useWorkspace.getState>['personById']>;
  onAdvance: () => void;
}) {
  const next = ADVANCE[lead.status];
  return <motion.div layout className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-card" data-id="element-222" data-mp="ei0c7rt3wayg">
      <div className="flex items-start justify-between" data-id="element-223" data-mp="olx7sxtgqrl2">
        <div className="min-w-0" data-id="element-224" data-mp="jlssvc87e4wy">
          <p className="truncate text-sm font-semibold text-slate-800" data-id="element-225" data-mp="jkrfh13pt6c2">{lead.contact}</p>
          <p className="truncate text-xs text-slate-400" data-id="element-226" data-mp="ely0f48s579s">{lead.company}</p>
        </div>
        <Avatar person={owner} size={26} data-id="element-227" data-mp="ale6sanf0lse" />
      </div>
      <p className="mt-2 text-xs text-slate-500" data-id="element-228" data-mp="amxjjr9v4epf">{lead.interest}</p>
      <div className="mt-3 flex items-center justify-between" data-id="element-229" data-mp="17abyfjzvc3lc">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600" data-id="element-230" data-mp="12cakfdhauugq">{lead.value}</span>
        {next && <button onClick={onAdvance} className="text-xs font-semibold text-brand-accent hover:underline" data-id="element-231" data-mp="xk99buvvb9sa">
            Move to {next} →
          </button>}
      </div>
      <p className="mt-2 text-[11px] text-slate-400" data-id="element-232" data-mp="1q2emvhir4o65">Added {formatDistanceToNow(new Date(lead.createdAt), {
        addSuffix: true
      })}</p>
    </motion.div>;
}